import React from 'react';
import {
  ResponsiveContainer,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Label
} from 'recharts';
import { StyledSkeletonWrapper, TooltipWrapper } from '~/pages/Dairy/Reporting/CoClimate/style';
import { Co2ReportDataType } from '~/api/services/reporting';
import { formatNumber } from '~/utils/helpers';
import { Nullable } from '~/types';
import { useAuth } from '~/routes/components/AuthProvider';
import { ADMIN_ROLE_SLUG } from '~/Constants';
import { DataFilteredType } from '../index';
import { emissionColors, getEmissionLabels } from '~/pages/Farmer/Dashboard/Tabs/CO2Dashboard/EmissionChart';
import PercenteIndicator from './PercenteIndicator';

interface Props {
  data: Nullable<Co2ReportDataType>
  averageTHGLabel: string
  barColors: {
    resultsColor: string,
    dairyResultsColor: string,
    defaultColor: string
  }
  isLoading: boolean
  subSupplierSelected: boolean
  resultsUnit: string
  dataFiltered: DataFilteredType
  handleChangeDataFiltered: (data: DataFilteredType) => void
}

const CustomTooltip = ({ active, payload, decimalNumber }: any) => {
  if (active && payload && payload.length) {
    return (
      <TooltipWrapper>
        {payload.map(chartValue => {
          const emissionLabels = getEmissionLabels()
          const label = emissionLabels[chartValue.name] ? emissionLabels[chartValue.name].name : chartValue.name
          return <div
            key={chartValue.name}
            style={{ color: chartValue.fill || chartValue.stroke, display: 'flex', alignItems: 'center', gap: 5 }}
          >
            <div>{label}:</div>
            <div style={{ fontWeight: 'bold' }} dangerouslySetInnerHTML={{ __html: `${formatNumber(chartValue.value, decimalNumber)} ${chartValue.unit}` }} />
          </div>
        })}
      </TooltipWrapper>
    );
  }
  return null;
};

const ResultsChart: React.FC<Props> = (props) => {
  const {
    isLoading,
    data,
    averageTHGLabel,
    barColors: {
      resultsColor,
      defaultColor,
      dairyResultsColor
    },
    dataFiltered
  } = props
  const { systemData: { logged: { roleSlug } } } = useAuth()

  const displayDecimal = dataFiltered?.resultType === 'sold_milk_economic_allocation_fpcm_idf'

  const splitBar = dataFiltered?.resultType === 'total'
  
  const splitDairyBar = splitBar &&
  'count' in data.dairyResults &&
  data.dairyResults.count > 9;

  const emissionLabels = getEmissionLabels()

  const adminLogged = ADMIN_ROLE_SLUG === roleSlug

  function getLabel(key: string) {
    switch (key) {
      case 'dairyResults':
        return averageTHGLabel
      case 'results':
        return adminLogged ? 'Landwirt(e)' : 'Mein Betrieb'
      case 'defaultResults':
        return 'IDB-Bayern Kuh (LfL)'
      default:
        return ''
    }
  }

  function getSplitBarData() {
    return Object.keys(data)?.map(key => {
      if(key === 'defaultResults') {
        return
      }
      if(key === 'dairyResults' && !splitDairyBar) {
        return
      }
      return {
        name: getLabel(key),
        inventoryAddition: data[key]?.['inventoryAddition'] ?? 0,
        calfRearing: data[key]?.['calfRearing'] ?? 0,
        feeding: data[key]?.['feeding'] ?? 0,
        manureAndBedding: data[key]?.['manureAndBedding'] ?? 0,
        waterAndEnergy: data[key]?.['waterAndEnergy'] ?? 0,
        dieselProductionAndCombustion: data[key]?.['dieselProductionAndCombustion'] ?? 0,
        total: data[key]?.['total'] ?? 0,
        key: key
      }

    }) || []
  }

  function getBarData() {
    return Object.keys(data)?.map(key => {
      if(key === 'defaultResults') {
        return
      }
      if(key === 'dairyResults' && data.dairyResults?.count < 10) {
        return
      }
      return {
        [key]: data[key]?.value,
        name: getLabel(key)
      }

    }) || []
  }

  const formattedData = data && splitBar ? getSplitBarData() : getBarData()

  const min = data ? Math.min(...Object.values(data).map(result => result?.total || result?.value).filter(data => !!data)) : 0;
  const max = data ? Math.max(...Object.values(data).map(result => result?.total || result?.value).filter(data => !!data)) : 1;
  const divider = Math.pow(10, Math.floor(max).toString().length - 3)

  const decimalNumber = displayDecimal ? 2 : 0

  function renderDairyResultsBar() {
    return <Bar
      unit={props.resultsUnit || 'kg CO<sub>2</sub>-Äq./kg Milch (FPCM)'}
      dataKey='dairyResults'
      stroke={dairyResultsColor}
      stackId={'a'}
      fill={dairyResultsColor}
      name={averageTHGLabel}
      label={{ position: 'top', fontSize: 15, fill: '#000', formatter: (value) => formatNumber(value, decimalNumber) }}
    />
  }

  function renderSplitBar() {
    const dairyResultsFormated = formattedData.find(data => data?.key === 'dairyResults')
    const sortedKeys = Object.keys(emissionLabels)
      .filter(key => !(key === 'total' || key === 'name'))
      .sort((a, b) => (dairyResultsFormated?.[b] ?? 0) - (dairyResultsFormated?.[a] ?? 0))

    return sortedKeys.map((key, index) => {
      if(index === sortedKeys.length - 1) {
        /*    return <Bar
          key={key}
          dataKey={key}
          stackId='a'
          fill={dairyResultsColor}
          unit={'kg CO<sub>2</sub>-Äq./Kuh u. Jahr'}
          label={{ position: 'top', fontSize: 15, fill: '#000', formatter: () => formatNumber(dairyResultsFormated?.total, 0) }}
        /> */
      }
      return <Bar
        key={key}
        unit={'kg CO<sub>2</sub>-Äq./Kuh u. Jahr'}
        dataKey={key}
        stackId='a'
        fill={emissionColors[key]}
      />
    })
  }

  function renderFarmerBar() {
    return <Bar
      unit={props.resultsUnit || 'kg CO<sub>2</sub>-Äq./kg Milch (FPCM)'}
      dataKey='results'
      stackId={'a'}
      fill={resultsColor}
      stroke={resultsColor}
      name='Mein Betrieb'
      label={{ position: 'top', fontSize: 15, fill: '#000', formatter: (value) => formatNumber(value, decimalNumber) }}
    />
  }

  function renderFarmerSplitBar() {
    const farmerResultsFormated = formattedData.find(data => data?.key === 'results')
    const sortedKeys = Object.keys(emissionLabels)
      .filter(key => !(key === 'total' || key === 'name'))
      .sort((a, b) => (farmerResultsFormated?.[b] ?? 0) - (farmerResultsFormated?.[a] ?? 0))

    return sortedKeys.map((key, index) => {
      if(index === sortedKeys.length - 1) {
        /*    return <Bar
          key={key}
          dataKey={key}
          stackId='a'
          fill={dairyResultsColor}
          unit={'kg CO<sub>2</sub>-Äq./Kuh u. Jahr'}
          label={{ position: 'top', fontSize: 15, fill: '#000', formatter: () => formatNumber(dairyResultsFormated?.total, 0) }}
        /> */
      }
      return <Bar
        key={key}
        unit={'kg CO<sub>2</sub>-Äq./Kuh u. Jahr'}
        dataKey={key}
        stackId='a'
        fill={emissionColors[key]}
      />
    })
  }

  function renderChart() {
    const farmerData = formattedData.length > 0 ? formattedData.find(item => item?.name === 'Mein Betrieb') : { results: 0 }
    const dairyData = formattedData.length > 0 ? formattedData.find(item => item?.name === averageTHGLabel) : { dairyResults: 1 }

    const dairyDataResults = dairyData ? dairyData['total'] || dairyData['dairyResults'] : null
    const farmerDataResults = farmerData ? farmerData['results'] : { results: 0 }

    console.log(formattedData.filter(data => !!data))

    return <BarChart
      data={formattedData.filter(data => !!data)}
      maxBarSize={70}
      margin={{
        top: 50,
        right: 30,
        left: 5,
        bottom: 5
      }}
    >
      <CartesianGrid strokeDasharray='3 3' />
      <XAxis dataKey='name' />
      <YAxis
        domain={[(min - divider) * 0.9, max + 3 * divider]}
        tickFormatter={(value) => formatNumber(value, decimalNumber)}
        allowDataOverflow={dataFiltered?.resultType !== 'total'}
      >
        <Label position='top' dx={45} dy={-30}>{props.resultsUnit}</Label>
      </YAxis>
      <Tooltip content={<CustomTooltip decimalNumber={decimalNumber}/>} cursor={false} />
      {splitBar ? renderFarmerSplitBar() : renderFarmerBar()}

      {dairyDataResults && <svg width='100%' height='100%'>
        <foreignObject
          x='45%'
          y='50%'
          width='180'
          height='100'
          style={{ transform: 'translate(-50px, -50px)', overflow: 'visible' }}
        >
          <PercenteIndicator
            value={formatNumber(farmerDataResults / dairyDataResults * 100 - 100, 1)}
            isPositive={parseFloat(dairyDataResults) > parseFloat(farmerDataResults)}
          />
        </foreignObject>
      </svg>
      }

      {/*  {!splitDairyBar && data?.dairyResults?.count > 9 && renderDairyResultsBar()}
      {splitDairyBar && data?.dairyResults?.count > 9 && renderSplitBar()} */}

      <Bar
        unit={props.resultsUnit || 'kg CO<sub>2</sub>-Äq./kg Milch (FPCM)'}
        stackId={'a'}
        fill={defaultColor}
        dataKey='defaultResults'
        stroke={defaultColor}
        name={'IDB-Bayern Kuh (LfL)'}
        label={{ position: 'top', fontSize: 15, fill: '#000', formatter: (value) => formatNumber(value, decimalNumber) }}
      />
    </BarChart>
  }

  function renderSkeleton() {
    return <StyledSkeletonWrapper>
      <div className='skeleton-chart'>
        <div className='skeleton-bar bar1'></div>
        <div className='skeleton-bar bar2'></div>
        <div className='skeleton-bar bar3'></div>
      </div>
    </StyledSkeletonWrapper>
  }
  return (
    <>
      <ResponsiveContainer width='100%' height={500}>
        {isLoading ? renderSkeleton() : renderChart()}
      </ResponsiveContainer>
    </>
  );
};

export default ResultsChart;
